import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Edit, CheckCircle2, AlertCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CorrectionDialog = ({ 
  open, 
  onOpenChange, 
  selectedRecord, 
  defaultTime,
  onCorrectionComplete 
}) => {
  const [correctionTime, setCorrectionTime] = useState(defaultTime || "18:00");
  const [correctionRemark, setCorrectionRemark] = useState("");
  const [correcting, setCorrecting] = useState(false);

  const handleCorrectPunchOut = async () => {
    if (!selectedRecord || !correctionTime) {
      toast.error("Please enter a valid punch-out time");
      return;
    }

    setCorrecting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/attendance/correct-punch-out/${selectedRecord.attendance_id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          punch_out_time: correctionTime,
          remark: correctionRemark || "Manual correction by admin"
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success("Punch-out corrected successfully");
        onOpenChange(false);
        setCorrectionRemark("");
        onCorrectionComplete?.();
        
        if (data.recalculated) {
          toast.info(`Working hours: ${data.recalculated.working_hours}h, Status: ${data.recalculated.status}`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to correct punch-out");
      }
    } catch (error) {
      toast.error("Error correcting punch-out");
    } finally {
      setCorrecting(false);
    }
  };

  // Reset form when dialog opens with new record
  const handleOpenChange = (isOpen) => {
    if (isOpen && defaultTime) {
      setCorrectionTime(defaultTime);
    }
    if (!isOpen) {
      setCorrectionRemark("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="correction-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5 text-blue-600" />
            Correct Missing Punch-Out
          </DialogTitle>
          <DialogDescription>
            Manually set the punch-out time for this attendance record.
          </DialogDescription>
        </DialogHeader>
        
        {selectedRecord && (
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
              <p className="text-sm font-medium">{selectedRecord.user_name || selectedRecord.user_email}</p>
              <p className="text-xs text-slate-500">
                {new Date(selectedRecord.date).toLocaleDateString("en-IN", {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric"
                })}
              </p>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                  Punch In: {selectedRecord.punch_in_time_display || 
                    (selectedRecord.punch_in_time ? new Date(selectedRecord.punch_in_time).toLocaleTimeString("en-IN", {
                      hour: "2-digit",
                      minute: "2-digit"
                    }) : "--:--")}
                </Badge>
              </div>
            </div>
            
            <div>
              <Label htmlFor="correction-time" className="text-sm font-medium">Punch-Out Time</Label>
              <Input
                id="correction-time"
                type="time"
                value={correctionTime}
                onChange={(e) => setCorrectionTime(e.target.value)}
                className="mt-1"
                data-testid="correction-time-input"
              />
            </div>
            
            <div>
              <Label htmlFor="correction-remark" className="text-sm font-medium">Remark (Optional)</Label>
              <Textarea
                id="correction-remark"
                value={correctionRemark}
                onChange={(e) => setCorrectionRemark(e.target.value)}
                placeholder="Reason for correction..."
                rows={2}
                className="mt-1"
              />
            </div>
            
            <div className="text-xs text-slate-500 bg-blue-50 dark:bg-blue-900/20 p-2 rounded flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span>Working hours and salary will be automatically recalculated based on the corrected time.</span>
            </div>
          </div>
        )}
        
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleCorrectPunchOut}
            disabled={correcting || !correctionTime}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {correcting ? "Correcting..." : (
              <>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Save Correction
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CorrectionDialog;
